import { useState, useEffect } from "react";
import {
  Check,
  Banknote,
  Calendar as CalendarIcon,
  AlertCircle,
} from "lucide-react";
import { loansService } from "../services/api";
import { toPersianNumbers, formatCurrency } from "../utils/numbers";
import { Modal } from "../components/Modal";

const getImageUrl = (url: string | undefined) => {
  if (!url) return "";
  const baseUrl = (import.meta as any).env?.VITE_API_BASE_URL || "";
  if (baseUrl && url.startsWith("/api/")) {
    // If baseUrl already ends with /api, we replace it, or we just construct it.
    // Usually VITE_API_BASE_URL is something like https://api.domain.com/api
    // Let's just strip /api from url and append to baseUrl
    return baseUrl.replace(/\/api\/?$/, "") + url;
  }
  return url;
};

interface Loan {
  id: number;
  user_id: number;
  amount: string | number;
  requested_date: string;
  description: string;
  status: string;
  first_name: string;
  last_name: string;
  national_code: string;
  mobile: string;
  registration_fee_amount: string | number;
  receipt_image?: string;
}

export default function LoanFeeReceipt() {
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const isAdmin = user?.role === "admin";

  const [loans, setLoans] = useState<Loan[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedLoanForReceipt, setSelectedLoanForReceipt] =
    useState<Loan | null>(null);
  const [selectedLoanForConfirm, setSelectedLoanForConfirm] =
    useState<Loan | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);

  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: "success" | "error" | "warning" | "info";
  }>({
    isOpen: false,
    title: "",
    message: "",
    type: "info",
  });

  useEffect(() => {
    fetchLoans();
  }, []);

  const fetchLoans = async () => {
    setLoading(true);
    try {
      const response = await loansService.getLoans();
      let loansArray = Array.isArray(response) ? response : response.data || [];
      // Only approved_registration loans waiting for fee
      loansArray = loansArray.filter(
        (loan: Loan) => loan.status === "approved_registration",
      );
      setLoans(loansArray);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching loans:", error);
      setLoans([]);
      setLoading(false);
    }
  };

  const handleConfirmFee = async () => {
    if (!selectedLoanForConfirm) return;
    setIsConfirming(true);
    try {
      await loansService.updateLoanStatus(
        selectedLoanForConfirm.id,
        "fee_paid",
      );
      setSelectedLoanForConfirm(null);
      setModalState({
        isOpen: true,
        title: "موفقیت",
        message: "کارمزد با موفقیت تایید و ذخیره شد. وام آماده پرداخت است.",
        type: "success",
      });
      fetchLoans();
    } catch (err: any) {
      setSelectedLoanForConfirm(null);
      setModalState({
        isOpen: true,
        title: "خطا",
        message: err.response?.data?.message || "خطا در برقراری ارتباط با سرور",
        type: "error",
      });
    } finally {
      setIsConfirming(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
            دریافت کارمزد ثبت نام اولیه وام
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            لیست وام‌های تایید شده که منتظر دریافت کارمزد هستند
          </p>
        </div>
        {!isAdmin && (
          <span className="px-3.5 py-1.5 bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800 rounded-xl text-xs font-semibold">
            حالت فقط مشاهده (سرمایه‌گذار)
          </span>
        )}
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-right">
            <thead className="bg-slate-50 dark:bg-slate-900/50 text-slate-500 dark:text-slate-400">
              <tr>
                <th className="p-4 font-medium whitespace-nowrap">متقاضی</th>
                <th className="p-4 font-medium whitespace-nowrap">کد ملی</th>
                <th className="p-4 font-medium whitespace-nowrap">موبایل</th>
                <th className="p-4 font-medium whitespace-nowrap">
                  مبلغ وام (تومان)
                </th>
                <th className="p-4 font-medium whitespace-nowrap">
                  کارمزد ثبت نام (تومان)
                </th>
                <th className="p-4 font-medium whitespace-nowrap">
                  تاریخ درخواست
                </th>
                <th className="p-4 font-medium whitespace-nowrap w-24">
                  عملیات
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
              {loading ? (
                <tr>
                  <td
                    colSpan={7}
                    className="p-8 text-center text-slate-500 dark:text-slate-400"
                  >
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-5 h-5 border-2 border-persian-500 border-t-transparent rounded-full animate-spin" />
                      <span>در حال بارگذاری...</span>
                    </div>
                  </td>
                </tr>
              ) : loans.length === 0 ? (
                <tr>
                  <td
                    colSpan={7}
                    className="p-8 text-center text-slate-500 dark:text-slate-400"
                  >
                    <AlertCircle className="w-12 h-12 mx-auto text-slate-300 dark:text-slate-600 mb-3" />
                    <p>هیچ وام در انتظار دریافت کارمزدی یافت نشد.</p>
                  </td>
                </tr>
              ) : (
                loans.map((loan) => (
                  <tr
                    key={loan.id}
                    className="hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors"
                  >
                    <td className="p-4 text-slate-900 dark:text-white font-medium">
                      {loan.first_name} {loan.last_name}
                    </td>
                    <td className="p-4 text-slate-600 dark:text-slate-300">
                      {toPersianNumbers(loan.national_code)}
                    </td>
                    <td className="p-4 text-slate-600 dark:text-slate-300">
                      {toPersianNumbers(loan.mobile)}
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2 text-slate-700 dark:text-slate-300 font-medium">
                        <Banknote className="w-4 h-4 text-slate-400" />
                        {formatCurrency(loan.amount)}
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2 text-persian-600 dark:text-persian-400 font-medium">
                        {formatCurrency(loan.registration_fee_amount || 0)}
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
                        <CalendarIcon className="w-4 h-4 text-slate-400" />
                        {toPersianNumbers(loan.requested_date)}
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex gap-2">
                        {loan.receipt_image && (
                          <button
                            onClick={() => setSelectedLoanForReceipt(loan)}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/40 rounded-lg text-sm font-medium transition-colors"
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                              ></path>
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                              ></path>
                            </svg>
                            فیش
                          </button>
                        )}
                        {isAdmin ? (
                          <button
                            type="button"
                            onClick={() => setSelectedLoanForConfirm(loan)}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-green-50 text-green-600 dark:bg-green-900/20 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/40 rounded-lg text-sm font-medium transition-colors"
                            title="تایید دریافت کارمزد"
                          >
                            <Check className="w-4 h-4" />
                            <span>تایید کارمزد</span>
                          </button>
                        ) : (
                          <span className="text-xs text-slate-400 font-medium px-2 py-1">
                            در انتظار تایید مدیر
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Modal
        isOpen={modalState.isOpen}
        onClose={() => setModalState({ ...modalState, isOpen: false })}
        title={modalState.title}
        message={modalState.message}
        type={modalState.type}
      />

      {/* مودال اخذ تاییدیه قبل از ثبت تایید کارمزد در دیتابیس */}
      <Modal
        isOpen={!!selectedLoanForConfirm}
        onClose={() => {
          if (!isConfirming) setSelectedLoanForConfirm(null);
        }}
        title="تایید دریافت کارمزد وام"
        type="confirm"
        confirmText="بله، تایید و ثبت در سامانه"
        cancelText="انصراف"
        isLoading={isConfirming}
        onConfirm={handleConfirmFee}
      >
        {selectedLoanForConfirm && (
          <div className="space-y-4 text-sm">
            <div className="p-3.5 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800/60 rounded-xl text-amber-800 dark:text-amber-300 text-xs sm:text-sm leading-relaxed">
              آیا از دریافت کارمزد ثبت‌نام اولیه این وام اطمینان دارید؟ با تایید
              این بخش، وضعیت وام به <strong>«کارمزد پرداخت شده»</strong> تغییر
              یافته و در دیتابیس ثبت می‌گردد.
            </div>

            <div className="bg-slate-50 dark:bg-slate-900/60 p-4 rounded-xl space-y-2.5 border border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300">
              <div className="flex justify-between items-center py-1 border-b border-slate-200/80 dark:border-slate-800">
                <span className="text-slate-500 dark:text-slate-400">
                  نام متقاضی:
                </span>
                <span className="font-bold text-slate-900 dark:text-white">
                  {selectedLoanForConfirm.first_name}{" "}
                  {selectedLoanForConfirm.last_name}
                </span>
              </div>
              <div className="flex justify-between items-center py-1 border-b border-slate-200/80 dark:border-slate-800">
                <span className="text-slate-500 dark:text-slate-400">
                  کد ملی:
                </span>
                <span>
                  {toPersianNumbers(selectedLoanForConfirm.national_code)}
                </span>
              </div>
              <div className="flex justify-between items-center py-1 border-b border-slate-200/80 dark:border-slate-800">
                <span className="text-slate-500 dark:text-slate-400">
                  شماره موبایل:
                </span>
                <span>{toPersianNumbers(selectedLoanForConfirm.mobile)}</span>
              </div>
              <div className="flex justify-between items-center py-1 border-b border-slate-200/80 dark:border-slate-800">
                <span className="text-slate-500 dark:text-slate-400">
                  مبلغ اصل وام:
                </span>
                <span className="font-semibold text-slate-900 dark:text-white">
                  {formatCurrency(selectedLoanForConfirm.amount)}
                </span>
              </div>
              <div className="flex justify-between items-center py-1 text-emerald-600 dark:text-emerald-400">
                <span className="font-medium">مبلغ کارمزد دریافتی:</span>
                <span className="font-bold text-base">
                  {formatCurrency(
                    selectedLoanForConfirm.registration_fee_amount || 0,
                  )}
                </span>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {selectedLoanForReceipt && selectedLoanForReceipt.receipt_image && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/80 backdrop-blur-sm"
          onClick={() => setSelectedLoanForReceipt(null)}
        >
          <div
            className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-700 w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center bg-slate-50 dark:bg-slate-900/50">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                تصویر فیش واریزی
              </h2>
              <button
                onClick={() => setSelectedLoanForReceipt(null)}
                className="p-2 text-slate-400 hover:text-red-500 rounded-lg bg-slate-100 dark:bg-slate-800 transition-colors"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  ></path>
                </svg>
              </button>
            </div>
            <div className="p-4 flex-1 overflow-auto flex justify-center bg-slate-100/50 dark:bg-slate-900/50">
              <img
                src={getImageUrl(selectedLoanForReceipt.receipt_image)}
                alt="فیش پرداختی"
                className="max-w-full max-h-full object-contain rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm"
              />
            </div>
            <div className="p-4 border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 flex justify-between items-center gap-3">
              <button
                type="button"
                onClick={() => {
                  const loan = selectedLoanForReceipt;
                  setSelectedLoanForReceipt(null);
                  setSelectedLoanForConfirm(loan);
                }}
                className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-sm font-medium transition-colors shadow-sm"
              >
                <Check className="w-4 h-4" />
                تایید کارمزد این وام
              </button>
              <a
                href={getImageUrl(selectedLoanForReceipt.receipt_image)}
                download={`${String(selectedLoanForReceipt.user_id).padStart(4, "0")}-${new Date().toLocaleDateString("en").replace(/\//g, "")}-${new Date().toTimeString().split(" ")[0].replace(/:/g, "")}.jpg`}
                className="inline-flex items-center gap-2 px-4 py-2 bg-persian-600 hover:bg-persian-700 text-white rounded-xl text-sm font-medium transition-colors"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                  ></path>
                </svg>
                دانلود تصویر
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
